// @ts-strict-ignore
import React, {useEffect, useRef} from 'react';
import clsx from 'clsx';

import {ButtonReact} from 'common/components/button';
import {SelectTheme} from 'common/components/select/constants';
import {SelectReact} from 'common/components/select';
import {ButtonTheme} from 'common/components/button/constants';
import {TooltipAttrs} from 'common/components/tooltip/types';
import {SectionItem} from 'common/components/items-menu/types';
import {MenuChangeEvent} from 'common/components/select/types';
import {MetricType} from 'common/query-builder/query-block/constants';
import {ResourceType} from 'common/report/constants';
import {Translatable} from 'common/i18n';
import {useTranslation} from 'common/i18n/hooks';

import {SelectorType} from '../types';

export function AddEntryRow(props: {
  hasContainedUI: boolean;
  hasPrefixRenderer: boolean;
  isDisabled: boolean;
  isOpen: boolean;
  tooltipAttrs: TooltipAttrs;
  typeItems: SectionItem[];
  addEntryButtonLabel: Translatable;
  addEntryMenuWidth: number;
  autofocus: boolean;
  onAddEntryRow: (type: SelectorType) => void;
  onAddTypeSelectMenuChange: (ev: MenuChangeEvent) => void;
}) {
  const ref = useRef(null);
  const {t, print} = useTranslation();

  useEffect(() => {
    if (props.autofocus) {
      ref.current?.focus();
    }
  }, []);

  const sections = [
    {items: props.typeItems.filter((item) => [ResourceType.User, ResourceType.Event].includes(item.value))},
    {
      items: props.typeItems.filter((item) =>
        [ResourceType.Cohort, MetricType.Funnel, MetricType.Retention].includes(item.value),
      ),
    },
  ];
  const singleEntryType = props.typeItems.length === 1 && props.typeItems[0].value;

  const buttonContents = (
    <>
      {singleEntryType ? (
        <ButtonReact
          ref={ref}
          className={clsx(`new-entry-button`, {
            'is-open': props.isOpen,
          })}
          disabled={props.isDisabled}
          theme={ButtonTheme.Tertiary}
          icon='plus'
          elref='newEntryButton'
          tooltip={!props.isDisabled && props.tooltipAttrs}
          onClick={() => props.onAddEntryRow(singleEntryType)}
        >
          <span>{print(props.addEntryButtonLabel) || t(`common.add`)}</span>
        </ButtonReact>
      ) : (
        <SelectReact
          ref={ref}
          className={clsx(`type-select`, {
            'is-open': props.isOpen,
          })}
          elref='typeSelect'
          disabled={props.isDisabled}
          icon={{leftIcon: `plus`, rightIcon: ``}}
          selectedOption={print(props.addEntryButtonLabel) || t(`common.add`)}
          menuWidth={props.addEntryMenuWidth}
          sections={sections}
          theme={SelectTheme.Tertiary}
          tooltip={!props.isDisabled ? props.tooltipAttrs : null}
          onChange={(ev: CustomEvent) => props.onAddEntryRow(ev.detail.item.value)}
          onMenuChange={props.onAddTypeSelectMenuChange}
        />
      )}
    </>
  );

  // contained sections (inside mp-section for instance) render as
  // another row whereas the new UI will render a button row
  if (props.hasContainedUI) {
    return buttonContents;
  }

  return (
    <div
      className={clsx(`mp-query-entry`, `is-add-row`, {
        'is-disabled': props.isDisabled,
        [`for-${singleEntryType}`]: Boolean(singleEntryType),
        'is-open': props.isOpen,
      })}
    >
      {/** include prefix spacing */}
      {props.hasPrefixRenderer && <div className='entry-selector-prefix' />}
      {buttonContents}
    </div>
  );
}
